function getFocusableElements (container) {
    return Array.from(
        container.querySelectorAll(
            "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
        ),
    );
}

class HTMLUpdateUtility {
    #preProcessCallbacks = [];
    #postProcessCallbacks = [];

    constructor () {}

    addPreProcessCallback (callback) {
        this.#preProcessCallbacks.push(callback);
    }

    addPostProcessCallback (callback) {
        this.#postProcessCallbacks.push(callback);
    }

    /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
    viewTransition (oldNode, newContent) {
        this.#preProcessCallbacks.forEach((callback) => callback(newContent));

        const newNodeWrapper = document.createElement('div');

        HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
        const newNode = newNodeWrapper.firstChild;

        oldNode.parentNode.insertBefore(newNode, oldNode);
        oldNode.style.display = 'none';

        this.#postProcessCallbacks.forEach((callback) => callback(newNode));

        setTimeout(() => oldNode.remove(), 1000);
    }

    // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    static setInnerHTML (element, html) {
        element.innerHTML = html;
        element.querySelectorAll('script').forEach((oldScriptTag) => {
            const newScriptTag = document.createElement('script');

            Array.from(oldScriptTag.attributes).forEach((attribute) => {
                newScriptTag.setAttribute(attribute.name, attribute.value);
            });
            newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
            oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
    }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

    if (summary.nextElementSibling.getAttribute('id')) {
        summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    }

    summary.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (summary.closest('header-drawer, menu-drawer')) { return; }
    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus (container, elementToFocus = container) {
    const elements = getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];

    removeTrapFocus();

    trapFocusHandlers.focusin = (event) => {
        if (event.target !== container && event.target !== last && event.target !== first) { return; }

        document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = function () {
        document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = function (event) {
        if (event.code.toUpperCase() !== 'TAB') { return; } // If not TAB key
        // On the last focusable element and tab forward, focus the first element.
        if (event.target === last && !event.shiftKey) {
            event.preventDefault();
            first.focus();
        }

        //  On the first focusable element and tab backward, focus the last element.
        if ((event.target === container || event.target === first) && event.shiftKey) {
            event.preventDefault();
            last.focus();
        }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    elementToFocus.focus();

    if (
        elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
    ) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
    document.querySelector(':focus-visible');
} catch (e) {
    focusVisiblePolyfill();
}

function focusVisiblePolyfill () {
    const navKeys = [
        'ARROWUP',
        'ARROWDOWN',
        'ARROWLEFT',
        'ARROWRIGHT',
        'TAB',
        'ENTER',
        'SPACE',
        'ESCAPE',
        'HOME',
        'END',
        'PAGEUP',
        'PAGEDOWN',
    ];
    let currentFocusedElement = null;
    let mouseClick = null;

    window.addEventListener('keydown', (event) => {
        if (navKeys.includes(event.code.toUpperCase())) {
            mouseClick = false;
        }
    });

    window.addEventListener('mousedown', (event) => {
        mouseClick = true;
    });

    window.addEventListener(
        'focus',
        () => {
            if (currentFocusedElement) { currentFocusedElement.classList.remove('focused'); }

            if (mouseClick) { return; }

            currentFocusedElement = document.activeElement;
            currentFocusedElement.classList.add('focused');
        },
        true,
    );
}

function pauseAllMedia () {
    document.querySelectorAll('.js-youtube').forEach((video) => {
        video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
    });
    document.querySelectorAll('.js-vimeo').forEach((video) => {
        video.contentWindow.postMessage('{"method":"pause"}', '*');
    });
    document.querySelectorAll('video').forEach((video) => video.pause());
    document.querySelectorAll('product-model').forEach((model) => {
        if (model.modelViewerUI) { model.modelViewerUI.pause(); }
    });
}

function removeTrapFocus (elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);

    if (elementToFocus) { elementToFocus.focus(); }
}

function onKeyUpEscape (event) {
    if (event.code.toUpperCase() !== 'ESCAPE') { return; }

    const openDetailsElement = event.target.closest('details[open]');

    if (!openDetailsElement) { return; }

    const summaryElement = openDetailsElement.querySelector('summary');

    openDetailsElement.removeAttribute('open');
    summaryElement.setAttribute('aria-expanded', false);
    summaryElement.focus();
}

class QuantityInput extends HTMLElement {
    constructor () {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));
        this.querySelectorAll('button').forEach((button) =>
            button.addEventListener('click', this.onButtonClick.bind(this)),
        );
    }

    quantityUpdateUnsubscriber = undefined;

    connectedCallback () {
        this.validateQtyRules();
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    }

    disconnectedCallback () {
        if (this.quantityUpdateUnsubscriber) {
            this.quantityUpdateUnsubscriber();
        }
    }

    onInputChange (event) {
        this.validateQtyRules();
    }

    onButtonClick (event) {
        event.preventDefault();
        const previousValue = this.input.value;

        if (event.target.name === 'plus') {
            if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
                this.input.value = this.input.dataset.min;
            } else {
                this.input.stepUp();

                if (this.parentElement.classList.contains('quantity-popover-container')) {
                    /* disables the btn until the qty has been changed */
                    event.target.disabled = true;
                }
            }
        } else {
            this.input.stepDown();

            if (this.parentElement.classList.contains('quantity-popover-container')) {
                /* disables the btn until the qty has been changed */
                event.target.disabled = true;
            }
        }

        if (previousValue !== this.input.value) { this.input.dispatchEvent(this.changeEvent); }

        if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
            this.input.value = parseInt(this.input.min);
        }
    }

    validateQtyRules () {
        const value = parseInt(this.input.value);

        if (this.input.min) {
            const buttonMinus = this.querySelector(".quantity__button[name='minus']");

            buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
        }
        if (this.input.max) {
            const max = parseInt(this.input.max);
            const buttonPlus = this.querySelector(".quantity__button[name='plus']");

            buttonPlus.classList.toggle('disabled', value >= max);
        }
    }
}

customElements.define('quantity-input', QuantityInput);

function debounce (fn, wait) {
    let t;

    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
    };
}

function throttle (fn, delay) {
    let lastCall = 0;

    return function (...args) {
        const now = new Date().getTime();

        if (now - lastCall < delay) {
            return;
        }
        lastCall = now;
        return fn(...args);
    };
}

function fetchConfig (type = 'json') {
    return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
    window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
    return function () {
        return fn.apply(scope, arguments);
    };
};

Shopify.setSelectorByValue = function (selector, value) {
    for (let i = 0, count = selector.options.length; i < count; i++) {
        const option = selector.options[i];

        if (value == option.value || value == option.innerHTML) {
            selector.selectedIndex = i;
            return i;
        }
    }
};

Shopify.addListener = function (target, eventName, callback) {
    target.addEventListener
        ? target.addEventListener(eventName, callback, false)
        : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
    options = options || {};
    const method = options.method || 'post';
    const params = options.parameters || {};

    const form = document.createElement('form');

    form.setAttribute('method', method);
    form.setAttribute('action', path);

    for (const key in params) {
        const hiddenField = document.createElement('input');

        hiddenField.setAttribute('type', 'hidden');
        hiddenField.setAttribute('name', key);
        hiddenField.setAttribute('value', params[key]);
        form.appendChild(hiddenField);
    }
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
    this.countryEl = document.getElementById(country_domid);
    this.provinceEl = document.getElementById(province_domid);
    this.provinceContainer = document.getElementById(options.hideElement || province_domid);

    Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

    this.initCountry();
    this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
    initCountry: function () {
        const value = this.countryEl.getAttribute('data-default');

        Shopify.setSelectorByValue(this.countryEl, value);
        this.countryHandler();
    },

    initProvince: function () {
        const value = this.provinceEl.getAttribute('data-default');

        if (value && this.provinceEl.options.length > 0) {
            Shopify.setSelectorByValue(this.provinceEl, value);
        }
    },

    countryHandler: function (e) {
        var opt = this.countryEl.options[this.countryEl.selectedIndex];
        const raw = opt.getAttribute('data-provinces');
        const provinces = JSON.parse(raw);

        this.clearOptions(this.provinceEl);
        if (provinces && provinces.length == 0) {
            this.provinceContainer.style.display = 'none';
        } else {
            for (let i = 0; i < provinces.length; i++) {
                var opt = document.createElement('option');

                opt.value = provinces[i][0];
                opt.innerHTML = provinces[i][1];
                this.provinceEl.appendChild(opt);
            }

            this.provinceContainer.style.display = '';
        }
    },

    clearOptions: function (selector) {
        while (selector.firstChild) {
            selector.removeChild(selector.firstChild);
        }
    },

    setOptions: function (selector, values) {
        for (let i = 0, count = values.length; i < values.length; i++) {
            const opt = document.createElement('option');

            opt.value = values[i];
            opt.innerHTML = values[i];
            selector.appendChild(opt);
        }
    },
};

class MenuDrawer extends HTMLElement {
    constructor () {
        super();

        this.mainDetailsToggle = this.querySelector('details');
        this.addEventListener('keyup', this.onKeyUp.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));
        this.bindEvents();
    }

    bindEvents () {
        this.querySelectorAll('summary').forEach((summary) =>
            summary.addEventListener('click', this.onSummaryClick.bind(this)),
        );
        this.querySelectorAll(
            'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)',
        ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }

    onKeyUp (event) {
        if (event.code.toUpperCase() !== 'ESCAPE') { return; }

        const openDetailsElement = event.target.closest('details[open]');

        if (!openDetailsElement) { return; }

        openDetailsElement === this.mainDetailsToggle
            ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
            : this.closeSubmenu(openDetailsElement);
    }

    onSummaryClick (event) {
        const summaryElement = event.currentTarget;
        const detailsElement = summaryElement.parentNode;
        const parentMenuElement = detailsElement.closest('.has-submenu');
        const isOpen = detailsElement.hasAttribute('open');
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        function addTrapFocus () {
            trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
            summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
        }

        if (detailsElement === this.mainDetailsToggle) {
            if (isOpen) { event.preventDefault(); }
            isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

            if (window.matchMedia('(max-width: 990px)')) {
                document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
            }
        } else {
            setTimeout(() => {
                detailsElement.classList.add('menu-opening');
                summaryElement.setAttribute('aria-expanded', true);
                parentMenuElement && parentMenuElement.classList.add('submenu-open');
                !reducedMotion || reducedMotion.matches
                    ? addTrapFocus()
                    : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
            }, 100);
        }
    }

    openMenuDrawer (summaryElement) {
        setTimeout(() => {
            this.mainDetailsToggle.classList.add('menu-opening');
        });
        summaryElement.setAttribute('aria-expanded', true);
        trapFocus(this.mainDetailsToggle, summaryElement);
        document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    }

    closeMenuDrawer (event, elementToFocus = false) {
        if (event === undefined) { return; }

        this.mainDetailsToggle.classList.remove('menu-opening');
        this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
            details.removeAttribute('open');
            details.classList.remove('menu-opening');
        });
        this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
            submenu.classList.remove('submenu-open');
        });
        document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
        removeTrapFocus(elementToFocus);
        this.closeAnimation(this.mainDetailsToggle);

        if (event instanceof KeyboardEvent) { elementToFocus?.setAttribute('aria-expanded', false); }
    }

    onFocusOut () {
        setTimeout(() => {
            if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) { this.closeMenuDrawer(); }
        });
    }

    onCloseButtonClick (event) {
        const detailsElement = event.currentTarget.closest('details');

        this.closeSubmenu(detailsElement);
    }

    closeSubmenu (detailsElement) {
        const parentMenuElement = detailsElement.closest('.submenu-open');

        parentMenuElement && parentMenuElement.classList.remove('submenu-open');
        detailsElement.classList.remove('menu-opening');
        detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
        removeTrapFocus(detailsElement.querySelector('summary'));
        this.closeAnimation(detailsElement);
    }

    closeAnimation (detailsElement) {
        let animationStart;

        const handleAnimation = (time) => {
            if (animationStart === undefined) {
                animationStart = time;
            }

            const elapsedTime = time - animationStart;

            if (elapsedTime < 400) {
                window.requestAnimationFrame(handleAnimation);
            } else {
                detailsElement.removeAttribute('open');
                if (detailsElement.closest('details[open]')) {
                    trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
                }
            }
        };

        window.requestAnimationFrame(handleAnimation);
    }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
    constructor () {
        super();
    }

    openMenuDrawer (summaryElement) {
        this.header = this.header || document.querySelector('.section-header');

        document.documentElement.style.setProperty(
            '--header-bottom-position',
            `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
        );
        this.header.classList.add('menu-open');

        setTimeout(() => {
            this.mainDetailsToggle.classList.add('menu-opening');
        });

        summaryElement.setAttribute('aria-expanded', true);
        window.addEventListener('resize', this.onResize);
        trapFocus(this.mainDetailsToggle, summaryElement);
        document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    }

    closeMenuDrawer (event, elementToFocus) {
        if (!elementToFocus) { return; }
        super.closeMenuDrawer(event, elementToFocus);
        this.header.classList.remove('menu-open');
        window.removeEventListener('resize', this.onResize);
    }

    onResize = () => {
        this.header &&
        document.documentElement.style.setProperty(
            '--header-bottom-position',
            `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
        );
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    };
}

customElements.define('header-drawer', HeaderDrawer);

class FooterAccordion extends HTMLElement {
    constructor () {
        super();
        this.accordionItem = this.querySelectorAll('.accordion-item');

        this.accordionItem.forEach((item) => {
            const toggle = item.querySelector('.accordion-toggle');
            const content = item.querySelector('.accordion-content');

            toggle.addEventListener('click', () => {
                toggle.classList.toggle('active');
                content.classList.toggle('hidden-mobile');
            });
        });
    }
}

customElements.define('footer-accordion', FooterAccordion);

class ModalDialog extends HTMLElement {
    constructor () {
        super();
        this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
        this.addEventListener('keyup', (event) => {
            if (event.code.toUpperCase() === 'ESCAPE') { this.hide(); }
        });
        if (this.classList.contains('media-modal')) {
            this.addEventListener('pointerup', (event) => {
                if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) { this.hide(); }
            });
        } else {
            this.addEventListener('click', (event) => {
                if (event.target === this) { this.hide(); }
            });
        }
    }

    connectedCallback () {
        if (this.moved) { return; }
        this.moved = true;
        document.body.appendChild(this);
    }

    show (opener) {
        this.openedBy = opener;
        const popup = this.querySelector('.template-popup');

        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
        if (popup) { popup.loadContent(); }
        trapFocus(this, this.querySelector('[role="dialog"]'));
        window.pauseAllMedia();
    }

    hide () {
        document.body.classList.remove('overflow-hidden');
        document.body.dispatchEvent(new CustomEvent('modalClosed'));
        this.removeAttribute('open');
        removeTrapFocus(this.openedBy);
        window.pauseAllMedia();
    }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
    constructor () {
        super();

        const button = this.querySelector('button');

        if (!button) { return; }
        button.addEventListener('click', () => {
            const modal = document.querySelector(this.getAttribute('data-modal'));

            if (modal) { modal.show(button); }
        });
    }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
    constructor () {
        super();
        const poster = this.querySelector('[id^="Deferred-Poster-"]');

        if (!poster) { return; }
        poster.addEventListener('click', this.loadContent.bind(this));
    }

    loadContent (focus = true) {
        window.pauseAllMedia();
        if (!this.getAttribute('loaded')) {
            const content = document.createElement('div');

            content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

            this.setAttribute('loaded', true);
            const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));

            if (focus) { deferredElement.focus(); }
            if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
                // force autoplay for safari
                deferredElement.play();
            }
        }
    }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
    constructor () {
        super();
        this.slider = this.querySelector('[id^="Slider-"]');
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.enableSliderLooping = false;
        this.currentPageElement = this.querySelector('.slider-counter--current');
        this.pageTotalElement = this.querySelector('.slider-counter--total');
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');

        if (!this.slider || !this.nextButton) { return; }

        this.initPages();
        const resizeObserver = new ResizeObserver((entries) => this.initPages());

        resizeObserver.observe(this.slider);

        this.slider.addEventListener('scroll', this.update.bind(this));
        this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
        this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    initPages () {
        this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
        if (this.sliderItemsToShow.length < 2) { return; }
        this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
        this.slidesPerPage = Math.floor(
            (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset,
        );
        this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
        this.update();
    }

    resetPages () {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.initPages();
    }

    update () {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
        if (!this.slider || !this.nextButton) { return; }

        const previousPage = this.currentPage;

        this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

        if (this.currentPageElement && this.pageTotalElement) {
            this.currentPageElement.textContent = this.currentPage;
            this.pageTotalElement.textContent = this.totalPages;
        }

        if (this.currentPage != previousPage) {
            this.dispatchEvent(
                new CustomEvent('slideChanged', {
                    detail: {
                        currentPage: this.currentPage,
                        currentElement: this.sliderItemsToShow[this.currentPage - 1],
                    },
                }),
            );
        }

        if (this.enableSliderLooping) { return; }

        if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
            this.prevButton.setAttribute('disabled', 'disabled');
        } else {
            this.prevButton.removeAttribute('disabled');
        }

        if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
            this.nextButton.setAttribute('disabled', 'disabled');
        } else {
            this.nextButton.removeAttribute('disabled');
        }
    }

    isSlideVisible (element, offset = 0) {
        const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;

        return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
    }

    onButtonClick (event) {
        event.preventDefault();
        const step = event.currentTarget.dataset.step || 1;

        this.slideScrollPosition =
      event.currentTarget.name === 'next'
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;
        this.setSlidePosition(this.slideScrollPosition);
    }

    setSlidePosition (position) {
        this.slider.scrollTo({
            left: position,
        });
    }
}

customElements.define('slider-component', SliderComponent);

class HalfProductSliderComponent extends HTMLElement {
    constructor () {
        super();

        const swiper = new Swiper(this, {
            slidesPerView: 1,
            autoplay: false,
            pagination: false,
            spaceBetween: 16,
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            breakpoints: {
                375: {
                    slidesPerView: 2,
                },
            },
        });
    }
}

customElements.define('half-product-slider-component', HalfProductSliderComponent);

class ProductSliderComponent extends HTMLElement {
    constructor () {
        super();

        const swiper = new Swiper(this, {
            slidesPerView: 1,
            autoplay: false,
            pagination: false,
            spaceBetween: 16,
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            breakpoints: {
                375: {
                    slidesPerView: 2,
                },
                768: {
                    slidesPerView: 3,
                },
                1024: {
                    slidesPerView: 4,
                },
            },
        });
    }
}

customElements.define('product-slider-component', ProductSliderComponent);

// Banner Slider CMS section
class SlideShowComponent extends HTMLElement {
    constructor () {
        super();

        const swiper = new Swiper(this, {
            slidesPerView: 1,
            spaceBetween: 16,
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
        });
    }
}

customElements.define('slideshow-component', SlideShowComponent);

// Announcement bar CMS section
class UspSliderComponent extends HTMLElement {
    constructor () {
        super();
        let swiper;
        const initSwiperSlider = (element) => {
            const swiperConfig = {
                slidesPerView: 1,
                slidesPerGroup: 1,
                loop: true,
                navigation: false,
                pagination: false,
                autoplay: {
                    delay: 3000,
                    disableOnInteraction: false,
                },
                breakpoints: {
                    768: {
                        slidesPerView: 2,
                        slidesPerGroup: 2,
                    },
                    1024: {
                        slidesPerView: 4,
                        slidesPerGroup: 4,
                        autoplay: false,
                    },
                },
            };

            const initSwiper = () => {
                swiper = new Swiper(element, swiperConfig);
            };

            const destroySwiper = () => {
                swiper?.destroy();
            };

            if (!window.matchMedia('(min-width: 1280px)').matches) {
                initSwiper();
            }

            window.addEventListener('resize', () => {
                if (window.matchMedia('(min-width: 1280px)').matches) {
                    destroySwiper();
                } else {
                    initSwiper();
                }
            });
        };

        initSwiperSlider(this);
    }
}

customElements.define('usp-slider-component', UspSliderComponent);

class VariantSelects extends HTMLElement {
    constructor () {
        super();
        this.addEventListener('change', this.handleProductUpdate);
        this.initializeProductSwapUtility();
    }

    initializeProductSwapUtility () {
        this.swapProductUtility = new HTMLUpdateUtility();
        this.swapProductUtility.addPreProcessCallback((html) => {
            html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
            return html;
        });
        this.swapProductUtility.addPostProcessCallback((newNode) => {
            window?.Shopify?.PaymentButton?.init();
            window?.ProductModel?.loadShopifyXR();
            publish(PUB_SUB_EVENTS.sectionRefreshed, {
                data: {
                    sectionId: this.dataset.section,
                    resource: {
                        type: SECTION_REFRESH_RESOURCE_TYPE.product,
                        id: newNode.querySelector('variant-selects').dataset.productId,
                    },
                },
            });
        });
    }

    handleProductUpdate (event) {
        const input = this.getInputForEventTarget(event.target);
        const targetId = input.id;
        const targetUrl = input.dataset.productUrl || this.dataset.url;

        this.currentVariant = this.getVariantData(targetId);
        const sectionId = this.dataset.originalSection || this.dataset.section;

        this.updateSelectedSwatchValue(event);
        this.toggleAddButton(true, '', false);
        this.removeErrorMessage();

        let callback = () => {};

        if (this.dataset.url !== targetUrl) {
            this.updateURL(targetUrl);
            this.updateShareUrl(targetUrl);
            callback = this.handleSwapProduct(sectionId);
        } else if (!this.currentVariant) {
            this.setUnavailable();
            callback = (html) => {
                this.updatePickupAvailability();
                this.updateOptionValues(html);
            };
        } else {
            this.updateURL(targetUrl);
            this.updateVariantInput();
            this.updateShareUrl(targetUrl);
            callback = this.handleUpdateProductInfo(sectionId);
        }

        this.renderProductInfo(sectionId, targetUrl, targetId, callback);
    }

    getSelectedOptionValues () {
        return Array.from(this.querySelectorAll('select, fieldset input:checked')).map(
            (element) => element.dataset.optionValueId,
        );
    }

    updateSelectedSwatchValue ({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
            const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
            const selectedDropdownSwatchValue = target
                .closest('.product-form__input')
                .querySelector('[data-selected-value] > .swatch');

            if (!selectedDropdownSwatchValue) { return; }
            if (swatchValue) {
                selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
                selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
            } else {
                selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
                selectedDropdownSwatchValue.classList.add('swatch--unavailable');
            }

            selectedDropdownSwatchValue.style.setProperty(
                '--swatch-focal-point',
                target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset',
            );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
            const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');

            if (selectedSwatchValue) { selectedSwatchValue.innerHTML = value; }
        }
    }

    updateMedia (html) {
        const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
        const mediaGallerySource = document.querySelector(`[id^="MediaGallery-${this.dataset.section}"] ul`);
        const mediaGalleryDestination = html.querySelector(`[id^="MediaGallery-${sectionId}"] ul`);

        const thumbnailGallerySource = document.querySelector('.thumbnail-slider ul');
        const thumbnailGalleryDestination = html.querySelector('.thumbnail-slider ul');

        const refreshSourceData = () => {
            const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));

            const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
            const sourceMap = new Map(mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]));

            return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        const refreshThumbSourceData = () => {
            const thumbnailGallerySourceItems = Array.from(thumbnailGallerySource.querySelectorAll('li[data-media-id]'));

            const thumbSourceSet = new Set(thumbnailGallerySourceItems.map((item) => item.dataset.mediaId));
            const thumbSourceMap = new Map(thumbnailGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]));

            return [thumbnailGallerySourceItems, thumbSourceSet, thumbSourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
            let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            const mediaGalleryDestinationItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
            const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
            let shouldRefresh = false;

            // add items from new data not present in DOM
            for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
                if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
                    mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
                    shouldRefresh = true;
                }
            }

            // remove items from DOM not present in new data
            for (let i = 0; i < mediaGallerySourceItems.length; i++) {
                if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
                    mediaGallerySourceItems[i].remove();
                    shouldRefresh = true;
                }
            }

            // refresh
            if (shouldRefresh) { [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData(); }

            // if media galleries don't match, sort to match new data order
            mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
                const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

                if (sourceData && sourceData.index !== destinationIndex) {
                    mediaGallerySource.insertBefore(
                        sourceData.item,
                        mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
                    );

                    // refresh source now that it has been modified
                    [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
                }
            });
        }

        // thumbnails

        if (thumbnailGallerySource && thumbnailGalleryDestination) {
            let [thumbnailGallerySourceItems, thumbnailSourceSet, thumbnailSourceMap] = refreshThumbSourceData();
            const thumbnailGalleryDestinationItems = Array.from(thumbnailGalleryDestination.querySelectorAll('li[data-media-id]'));
            const thumbnailDestinationSet = new Set(thumbnailGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
            let shouldRefreshThumbs = false;

            // add items from new data not present in DOM
            for (let i = thumbnailGalleryDestinationItems.length - 1; i >= 0; i--) {
                if (!thumbnailSourceSet.has(thumbnailGalleryDestinationItems[i].dataset.mediaId)) {
                    thumbnailGallerySource.prepend(thumbnailGalleryDestinationItems[i]);
                    shouldRefreshThumbs = true;
                }
            }

            // remove items from DOM not present in new data
            for (let i = 0; i < thumbnailGallerySourceItems.length; i++) {
                if (!thumbnailDestinationSet.has(thumbnailGallerySourceItems[i].dataset.mediaId)) {
                    thumbnailGallerySourceItems[i].remove();
                    shouldRefreshThumbs = true;
                }
            }

            // refresh
            if (shouldRefreshThumbs) { [thumbnailGallerySourceItems, thumbnailSourceSet, thumbnailSourceMap] = refreshThumbSourceData(); }

            // if media galleries don't match, sort to match new data order
            thumbnailGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
                const thumbnailSourceData = thumbnailSourceMap.get(destinationItem.dataset.mediaId);

                if (thumbnailSourceData && thumbnailSourceData.index !== destinationIndex) {
                    thumbnailGallerySource.insertBefore(
                        thumbnailSourceData.item,
                        thumbnailGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
                    );

                    // refresh source now that it has been modified
                    [thumbnailGallerySourceItems, thumbnailSourceSet, thumbnailSourceMap] = refreshThumbSourceData();
                }
            });
        }

        if (this.currentVariant.featured_media) {
            document
                .querySelector(`[id^="MediaGallery-${this.dataset.section}"]`)
                ?.setActiveMedia?.(`${this.dataset.section}-${this.currentVariant.featured_media.id}`);
        }

        // update media modal
        const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
        const newModalContent = html.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);

        if (modalContent && newModalContent) { modalContent.innerHTML = newModalContent.innerHTML; }
    }

    updateURL (url) {
        if (this.dataset.updateUrl === 'false') { return; }
        window.history.replaceState({}, '', `${url}${this.currentVariant?.id ? `?variant=${this.currentVariant.id}` : ''}`);
    }

    updateShareUrl (url) {
        const shareButton = document.getElementById(`Share-${this.dataset.section}`);

        if (!shareButton || !shareButton.updateUrl) { return; }
        shareButton.updateUrl(
            `${window.shopUrl}${url}${this.currentVariant?.id ? `?variant=${this.currentVariant.id}` : ''}`,
        );
    }

    updateVariantInput () {
        const productForms = document.querySelectorAll(
            `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        );

        productForms.forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');

            input.value = this.currentVariant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    updatePickupAvailability () {
        const pickUpAvailability = document.querySelector('pickup-availability');

        if (!pickUpAvailability) { return; }

        if (this.currentVariant && this.currentVariant.available) {
            pickUpAvailability.fetchAvailability(this.currentVariant.id);
        } else {
            pickUpAvailability.removeAttribute('available');
            pickUpAvailability.innerHTML = '';
        }
    }

    getInputForEventTarget (target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }

    getVariantData (inputId) {
        return JSON.parse(this.getVariantDataElement(inputId).textContent);
    }

    getVariantDataElement (inputId) {
        return this.querySelector(`script[type="application/json"][data-resource="${inputId}"]`);
    }

    removeErrorMessage () {
        const section = this.closest('section');

        if (!section) { return; }

        const productForm = section.querySelector('product-form');

        if (productForm) { productForm.handleErrorMessage(); }
    }

    getWrappingSection (sectionId) {
        return (
            this.closest(`section[data-section="${sectionId}"]`) || // main-product
      this.closest(`quick-add-modal`)?.modalContent || // quick-add
      this.closest(`#shopify-section-${sectionId}`) || // featured-product
      null
        );
    }

    handleSwapProduct (sectionId) {
        return (html) => {
            const oldContent = this.getWrappingSection(sectionId);

            if (!oldContent) {
                return;
            }

            document.getElementById(`ProductModal-${sectionId}`)?.remove();

            const response =
        html.querySelector(`section[data-section="${sectionId}"]`) /* main/quick-add */ ||
        html.getElementById(`shopify-section-${sectionId}`); /* featured product */

            this.swapProductUtility.viewTransition(oldContent, response);
        };
    }

    handleUpdateProductInfo (sectionId) {
        return (html) => {
            this.updatePickupAvailability();
            this.updateMedia(html);
            const priceDestination = document.getElementById(`price-${this.dataset.section}`);
            const priceSource = html.getElementById(`price-${sectionId}`);
            const skuSource = html.getElementById(`Sku-${sectionId}`);
            const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
            const inventorySource = html.getElementById(`Inventory-${sectionId}`);
            const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

            const volumePricingSource = html.getElementById(`Volume-${sectionId}`);

            const pricePerItemDestination = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
            const pricePerItemSource = html.getElementById(`Price-Per-Item-${sectionId}`);

            const volumePricingDestination = document.getElementById(`Volume-${this.dataset.section}`);
            const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);
            const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);

            if (volumeNote) { volumeNote.classList.remove('hidden'); }
            if (volumePricingDestination) { volumePricingDestination.classList.remove('hidden'); }
            if (qtyRules) { qtyRules.classList.remove('hidden'); }
            if (priceSource && priceDestination) {
                priceDestination.innerHTML = priceSource.innerHTML;
            }
            if (inventorySource && inventoryDestination) { inventoryDestination.innerHTML = inventorySource.innerHTML; }
            if (skuSource && skuDestination) {
                skuDestination.innerHTML = skuSource.innerHTML;
                skuDestination.classList.toggle('hidden', skuSource.classList.contains('hidden'));
            }
            if (volumePricingSource && volumePricingDestination) {
                volumePricingDestination.innerHTML = volumePricingSource.innerHTML;
            }
            if (pricePerItemSource && pricePerItemDestination) {
                pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML;
                pricePerItemDestination.classList.toggle('hidden', pricePerItemSource.classList.contains('hidden'));
            }

            const price = document.getElementById(`price-${this.dataset.section}`);

            if (price) { price.classList.remove('hidden'); }

            if (inventoryDestination) { inventoryDestination.classList.toggle('hidden', inventorySource.innerText === ''); }

            const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);

            this.toggleAddButton(
                addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
                window.variantStrings.soldOut,
            );

            this.updateOptionValues(html);

            publish(PUB_SUB_EVENTS.variantChange, {
                data: {
                    sectionId,
                    html,
                    variant: this.currentVariant,
                },
            });
        };
    }

    updateOptionValues (html) {
        const variantSelects = html.querySelector('variant-selects');

        if (variantSelects) { this.innerHTML = variantSelects.innerHTML; }
    }

    renderProductInfo (sectionId, url, targetId, callback) {
        const params = this.currentVariant
            ? `variant=${this.currentVariant?.id}`
            : `option_values=${this.getSelectedOptionValues().join(',')}`;

        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(`${url}?section_id=${sectionId}&${params}`, {
            signal: this.abortController.signal,
        })
            .then((response) => response.text())
            .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');

                callback(html);
            })
            .then(() => {
                // set focus to last clicked option value
                document.getElementById(targetId).focus();
            });
    }

    toggleAddButton (disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);

        if (!productForm) { return; }
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');

        if (!addButton) { return; }

        if (disable) {
            addButton.setAttribute('disabled', 'disabled');
            if (text) { addButtonText.textContent = text; }
        } else {
            addButton.removeAttribute('disabled');
            addButtonText.textContent = window.variantStrings.addToCart;
        }
    }

    setUnavailable () {
        this.toggleAddButton(true, '', true);
        const button = document.getElementById(`product-form-${this.dataset.section}`);
        const addButton = button.querySelector('[name="add"]');
        const addButtonText = button.querySelector('[name="add"] > span');
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);
        const pricePerItem = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
        const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);
        const volumeTable = document.getElementById(`Volume-${this.dataset.section}`);
        const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);

        if (!addButton) { return; }
        addButtonText.textContent = window.variantStrings.unavailable;
        if (price) { price.classList.add('hidden'); }
        if (inventory) { inventory.classList.add('hidden'); }
        if (sku) { sku.classList.add('hidden'); }
        if (pricePerItem) { pricePerItem.classList.add('hidden'); }
        if (volumeNote) { volumeNote.classList.add('hidden'); }
        if (volumeTable) { volumeTable.classList.add('hidden'); }
        if (qtyRules) { qtyRules.classList.add('hidden'); }
    }

    getInputSelector () {
        return 'variant-selects fieldset input[type="radio"], variant-selects select option';
    }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
    observer = undefined;

    constructor () {
        super();
    }

    connectedCallback () {
        this.initializeRecommendations();

        this.unsubscribeFromSectionRefresh = subscribe(PUB_SUB_EVENTS.sectionRefreshed, (event) => {
            const sectionId = this.dataset.sectionId;
            const isRelatedProduct = this.classList.contains('related-products');
            const isParentSectionUpdated = sectionId && (event.data?.sectionId ?? '') === `${sectionId.split('__')[0]}__main`;

            if (isRelatedProduct && isParentSectionUpdated) {
                this.dataset.productId = event.data.resource.id;
                this.initializeRecommendations();
            }
        });
    }

    disconnectedCallback () {
        this.unsubscribeFromSectionRefresh();
    }

    initializeRecommendations () {
        this.observer?.unobserve(this);
        this.observer = new IntersectionObserver(
            (entries, observer) => {
                if (!entries[0].isIntersecting) { return; }
                observer.unobserve(this);
                this.loadRecommendations();
            },
            { rootMargin: '0px 0px 400px 0px' },
        );
        this.observer.observe(this);
    }

    loadRecommendations () {
        fetch(`${this.dataset.url}&product_id=${this.dataset.productId}&section_id=${this.dataset.sectionId}`)
            .then((response) => response.text())
            .then((text) => {
                const html = document.createElement('div');

                html.innerHTML = text;
                const recommendations = html.querySelector('product-recommendations');

                if (recommendations?.innerHTML.trim().length) {
                    this.innerHTML = recommendations.innerHTML;
                }
            })
            .catch((e) => {
                console.error(e);
            });
    }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
    constructor () {
        super();

        this.icon = this.querySelector('.icon');
    }

    connectedCallback () {
        document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
    }

    handleStorefrontSignInCompleted (event) {
        if (event?.detail?.avatar) {
            this.icon?.replaceWith(event.detail.avatar.cloneNode());
        }
    }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
    constructor () {
        super();
        this.queue = [];
        this.requestStarted = false;
        this.ids = [];
    }

    startQueue (id, quantity) {
        this.queue.push({ id, quantity });
        const interval = setInterval(() => {
            if (this.queue.length > 0) {
                if (!this.requestStarted) {
                    this.sendRequest(this.queue);
                }
            } else {
                clearInterval(interval);
            }
        }, 250);
    }

    sendRequest (queue) {
        this.requestStarted = true;
        const items = {};

        queue.forEach((queueItem) => {
            items[parseInt(queueItem.id)] = queueItem.quantity;
        });
        this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
        const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');

        quickBulkElement.updateMultipleQty(items);
    }

    resetQuantityInput (id) {
        const input = this.querySelector(`#Quantity-${id}`);

        input.value = input.getAttribute('value');
        this.isEnterPressed = false;
    }

    setValidity (event, index, message) {
        event.target.setCustomValidity(message);
        event.target.reportValidity();
        this.resetQuantityInput(index);
        event.target.select();
    }

    validateQuantity (event) {
        const inputValue = parseInt(event.target.value);
        const index = event.target.dataset.index;

        if (inputValue < event.target.dataset.min) {
            this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
        } else if (inputValue > parseInt(event.target.max)) {
            this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
        } else if (inputValue % parseInt(event.target.step) != 0) {
            this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
        } else {
            event.target.setCustomValidity('');
            event.target.reportValidity();
            this.startQueue(index, inputValue);
        }
    }

    getSectionsUrl () {
        if (window.pageNumber) {
            return `${window.location.pathname}?page=${window.pageNumber}`;
        }
        return `${window.location.pathname}`;
    }

    getSectionInnerHTML (html, selector) {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
}

if (!customElements.get('bulk-add')) {
    customElements.define('bulk-add', BulkAdd);
}
